import React, { useState, useEffect, useRef } from 'react';
import { X, Ambulance, User, Phone, Clock } from 'lucide-react';
import hospitalService from '../../services/hospitalService';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useAllocationWebSocket } from '../../hooks/useAllocationWebSocket';

/**
 * AutomaticAssignmentPopup Component
 * Displays when an ambulance is automatically assigned during automatic flow
 * Shows ambulance details, driver info, and timer
 * Polls allocation status API for real-time updates
 */
const AutomaticAssignmentPopup = ({
  isOpen,
  onClose,
  ambulance,
  driver,
  initialTimer = 30,
  bookingId = null
}) => {
  const [timer, setTimer] = useState(initialTimer);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const timerStartTimeRef = useRef(null); // Store when timer started (timestamp-based)
  const lastDisplayedTimerRef = useRef(null); // Track last displayed timer value to prevent unnecessary updates
  const expiresAtRef = useRef(null); // Store absolute expiration time from API
  const { user } = useAuth();
  
  // Helper: Check if user is a fleet user
  const isFleetUser = () => {
    return user?.role === 'TENANT_ADMIN' ||
           user?.role === 'TENANT_USER' ||
           user?.role === 'tenant_admin' ||
           user?.role === 'tenant_user' ||
           user?.role === 'Fleet' ||
           user?.role === 'fleet';
  };

  // WebSocket integration: Listen for allocation status changes and trigger refetch
  useAllocationWebSocket(
    bookingId,
    () => pollAllocationStatus(bookingId),
    isOpen && !!bookingId
  );

  // Poll allocation status API
  useEffect(() => {
    console.log('🔍 [AutomaticAssignmentPopup] Poll effect triggered:', { isOpen, bookingId });
    
    if (isOpen && bookingId) {
      console.log('🔄 [AutomaticAssignmentPopup] Starting to poll allocation status for booking:', bookingId);
      setIsPolling(true);
      
      // Poll immediately first time
      pollAllocationStatus(bookingId);
      
      // Reduced polling frequency: WebSocket handles real-time updates, polling is fallback
      // Poll every 10 seconds (instead of 3s) since WebSocket provides instant updates
      pollingIntervalRef.current = setInterval(() => {
        console.log('🔄 [AutomaticAssignmentPopup] Polling interval triggered for booking:', bookingId);
        pollAllocationStatus(bookingId);
      }, 10000); // Increased from 3000ms to 10000ms - WebSocket handles real-time
      
      // Stop polling after 2 minutes (12 polls instead of 40)
      setTimeout(() => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setIsPolling(false);
          console.log('⏱️ [AutomaticAssignmentPopup] Polling stopped after timeout');
        }
      }, 120000);
    } else {
      console.log('⚠️ [AutomaticAssignmentPopup] Polling not started:', { isOpen, bookingId });
      // Stop polling when modal closes or bookingId is missing
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsPolling(false);
        console.log('🛑 [AutomaticAssignmentPopup] Polling stopped');
      }
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        console.log('🧹 [AutomaticAssignmentPopup] Cleaning up polling interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isOpen, bookingId]);

  // Poll allocation status API
  // Note: Timer is UI-managed only, not updated from backend
  const pollAllocationStatus = async (bookingId) => {
    try {
      console.log('🔍 [AutomaticAssignmentPopup] Polling allocation status for booking:', bookingId);
      // Use fleet endpoint for fleet users, hospital endpoint for hospital users
      const response = isFleetUser()
        ? await authService.fleetGetAllocationStatus(bookingId)
        : await hospitalService.getAllocationStatus(bookingId);
      
      if (response.success && response.data) {
        const statusData = response.data;
        console.log('📥 [AutomaticAssignmentPopup] Allocation status received:', statusData);
        
        // Normalize response structure for fleet API (which has response_id nested in driver_response)
        // Fleet API: driver_response.response_id, Hospital API: response_id at top level
        const normalizedStatusData = isFleetUser() ? {
          ...statusData,
          // Extract response_id from driver_response for fleet API
          response_id: statusData.driver_response?.response_id || statusData.response_id,
          // Ensure driver_response structure is consistent
          driver_response: {
            ...statusData.driver_response,
            response_id: statusData.driver_response?.response_id || statusData.response_id,
            status: statusData.driver_response?.status || statusData.ui_state,
            expires_in_sec: statusData.driver_response?.expires_in_sec,
            responded_at: statusData.driver_response?.responded_at
          }
        } : statusData;
        
        // Update allocation status with normalized data for real-time UI binding
        setAllocationStatus(prevStatus => ({
          ...prevStatus,
          ...normalizedStatusData,
          // Use latest driver and vehicle data from API (real-time updates)
          driver: normalizedStatusData.driver || prevStatus?.driver,
          vehicle: normalizedStatusData.vehicle || prevStatus?.vehicle,
          // Preserve allocation_type
          allocation_type: normalizedStatusData.allocation_type || prevStatus?.allocation_type || 'AUTOMATIC',
          // Ensure response_id is available at top level
          response_id: normalizedStatusData.response_id || normalizedStatusData.driver_response?.response_id || prevStatus?.response_id
        }));
        
        // CRITICAL: Store expires_at for timer synchronization (per integration doc)
        if (normalizedStatusData.driver_response?.expires_at) {
          expiresAtRef.current = new Date(normalizedStatusData.driver_response.expires_at).getTime();
          console.log('⏱️ [AutomaticAssignmentPopup] Stored expires_at for timer sync:', normalizedStatusData.driver_response.expires_at);
        }
        
        console.log('📊 [AutomaticAssignmentPopup] Real-time allocation data bound to UI:', {
          booking_id: normalizedStatusData.booking_id,
          allocation_type: normalizedStatusData.allocation_type,
          ui_state: normalizedStatusData.ui_state,
          driver_response_status: normalizedStatusData.driver_response?.status,
          hasDriver: !!normalizedStatusData.driver,
          hasVehicle: !!normalizedStatusData.vehicle,
          driverName: normalizedStatusData.driver?.name,
          vehicleNumber: normalizedStatusData.vehicle?.number,
          expires_at: normalizedStatusData.driver_response?.expires_at
        });
      }
    } catch (error) {
      console.error('❌ [AutomaticAssignmentPopup] Error polling allocation status:', error);
    }
  };

  // Timer countdown using expires_at from API (per integration doc requirement)
  // Falls back to initialTimer if expires_at is not available
  useEffect(() => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
      timerStartTimeRef.current = null;
      lastDisplayedTimerRef.current = null;
    }

    if (!isOpen) {
      return;
    }

    // PRIORITY: Use expires_at from API if available (per integration doc)
    // FALLBACK: Use initialTimer for backwards compatibility
    const updateTimer = () => {
      let remaining = 0;
      
      if (expiresAtRef.current) {
        // Use expires_at (absolute time) for accurate countdown
        const now = Date.now();
        remaining = Math.max(0, Math.floor((expiresAtRef.current - now) / 1000));
      } else if (initialTimer !== undefined && initialTimer !== null && timerStartTimeRef.current) {
        // Fallback: Use initialTimer with timestamp-based countdown
        const elapsed = Math.floor((Date.now() - timerStartTimeRef.current) / 1000);
        remaining = Math.max(0, initialTimer - elapsed);
      } else {
        // No timer data available
        return;
      }
      
      // Only update state when the second value actually changes
      if (remaining !== lastDisplayedTimerRef.current) {
        lastDisplayedTimerRef.current = remaining;
        setTimer(remaining);
      }
      
      if (remaining <= 0) {
        // Timer expired
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        timerStartTimeRef.current = null;
        expiresAtRef.current = null;
      }
    };

    // Initialize timer
    if (expiresAtRef.current) {
      // Use expires_at - no need to set start time
      lastDisplayedTimerRef.current = Math.max(0, Math.floor((expiresAtRef.current - Date.now()) / 1000));
      setTimer(lastDisplayedTimerRef.current);
    } else if (initialTimer !== undefined && initialTimer !== null && initialTimer > 0) {
      // Fallback: Use initialTimer with timestamp-based countdown
      timerStartTimeRef.current = Date.now();
      lastDisplayedTimerRef.current = initialTimer;
      setTimer(initialTimer);
    } else {
      return;
    }
    
    // Update immediately
    updateTimer();
    
    // Use 100ms interval for smooth updates
    timerIntervalRef.current = setInterval(updateTimer, 100);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        timerStartTimeRef.current = null;
        lastDisplayedTimerRef.current = null;
      }
    };
  }, [isOpen, initialTimer, allocationStatus?.driver_response?.expires_at]); // Re-run when expires_at changes

  // Sync timer with prop changes (external control from parent)
  // Note: Timer is UI-managed only - parent always passes UI-managed countdown (30 or counting down)
  // This is NOT synced from backend - strictly UI-side management
  useEffect(() => {
    if (initialTimer !== undefined && initialTimer !== null && isOpen) {
      // Timer value comes from parent's UI-managed countdown, not from backend
      // Reset timer start time when initialTimer changes
      timerStartTimeRef.current = Date.now();
      setTimer(initialTimer);
    }
  }, [initialTimer, isOpen]);

  // Handle close button
  const handleClose = async () => {
    console.log('❌ [Automatic Assignment Popup] Cancel clicked - stopping allocation process');
    
    // Stop polling when cancel is clicked
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
      console.log('🛑 [Automatic Assignment Popup] Polling stopped');
    }
    
    // Stop timer when cancel is clicked
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
      console.log('🛑 [Automatic Assignment Popup] Timer stopped');
    }
    
    // Call stop-allocation API endpoint
    if (bookingId) {
      try {
        console.log('🛑 [Automatic Assignment Popup] Calling stop-allocation API for bookingId:', bookingId);
        // Use fleet endpoint for fleet users, hospital endpoint for hospital users
        const response = isFleetUser()
          ? await authService.fleetStopAllocation(bookingId)
          : await hospitalService.stopAllocation(bookingId);
        console.log('✅ [Automatic Assignment Popup] Stop-allocation API response:', response);
        
        // The API response contains real-time data about the stopped allocation
        // Response structure: { success: true, message: "...", data: { success: true, message: "..." } }
        if (response?.data) {
          console.log('✅ [Automatic Assignment Popup] Allocation stopped successfully:', response.data);
        }
      } catch (error) {
        console.error('❌ [Automatic Assignment Popup] Error calling stop-allocation API:', error);
        // Continue with closing even if API call fails
      }
    } else {
      console.warn('⚠️ [Automatic Assignment Popup] No bookingId available for stop-allocation API call');
    }
    
    // Close the popup
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-start justify-start z-[60] p-4 pt-16"
      onClick={(e) => {
        // Prevent closing on backdrop click
        e.stopPropagation();
      }}
    >
      <div className="bg-white rounded-lg max-w-md w-full ml-4 shadow-xl overflow-hidden max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-blue-50 p-4 rounded-t-lg border-b border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-blue-100 p-1.5 rounded-full">
                <Ambulance className="w-4 h-4 text-blue-600" />
              </div>
              <div className="ml-2">
                <h3 className="text-base font-semibold text-blue-900">
                  Contacting Driver
                </h3>
                <p className="text-xs text-blue-700">
                  Waiting for driver to accept the request
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-blue-400 hover:text-blue-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Timer Display */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-center gap-2">
            <Clock className={`w-5 h-5 text-blue-600 ${timer > 0 ? 'animate-pulse' : ''}`} />
            <span className="text-lg font-bold text-blue-900 font-mono">
              {timer > 0 ? `${timer}s` : 'Expired'}
            </span>
            <span className="text-sm text-blue-700">
              {timer > 0 ? 'remaining to decide' : 'moving to next ambulance...'}
            </span>
          </div>

          {/* Ambulance Type Details - Use ambulance prop for consistency with Active card */}
          {ambulance && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Ambulance className="w-4 h-4 text-gray-600" />
                Ambulance Details
              </h4>
              <div className="space-y-2">
                <div>
                  <span className="text-xs text-gray-500">Type:</span>
                  <span className="text-sm font-medium text-gray-900 ml-2">
                    {ambulance?.name || ambulance?.type || 'N/A'}
                  </span>
                </div>
                {(ambulance?.description) && (
                  <div>
                    <span className="text-xs text-gray-500">Description:</span>
                    <p className="text-xs text-gray-700 mt-1">
                      {ambulance.description}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-gray-500">Estimated Fare:</span>
                  <span className="text-sm font-bold text-gray-900 ml-2">
                    ₹{ambulance?.estimated_fare ? Math.round(ambulance.estimated_fare) : (ambulance?.price ? Math.round(ambulance.price) : 'N/A')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Driver Information - Use driver prop from parent (synced with active card), fallback to allocationStatus */}
          {(() => {
            // CRITICAL: Prioritize driver prop from parent (which comes from availableAmbulanceTypes[currentAutomaticIndex])
            // This ensures modal shows the same driver as the active card in automatic allocation UI
            // Only use allocationStatus as fallback if driver prop is not available
            const displayDriver = driver || allocationStatus?.driver;
            
            return displayDriver && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <h4 className="text-xs font-semibold text-blue-700 mb-3">
                  Driver Information
                  {!driver && allocationStatus?.driver && (
                    <span className="ml-2 text-xs text-green-600 font-normal">(Live)</span>
                  )}
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-600" />
                    <div>
                      <span className="text-xs text-blue-600">Driver Name:</span>
                      <span className="text-sm font-medium text-blue-900 ml-2">
                        {displayDriver?.name || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-600" />
                    <div>
                      <span className="text-xs text-blue-600">Mobile Number:</span>
                      <span className="text-sm font-medium text-blue-900 ml-2">
                        {displayDriver?.phone || displayDriver?.phone_number || 'N/A'}
                      </span>
                    </div>
                  </div>
                  {displayDriver?.rating && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-600">Rating:</span>
                      <span className="text-sm font-medium text-blue-900 ml-2">
                        {displayDriver.rating} ⭐
                      </span>
                    </div>
                  )}
                  {(displayDriver?.ambulanceNumber || allocationStatus?.vehicle?.number) && (
                    <div className="flex items-center gap-2">
                      <Ambulance className="w-4 h-4 text-blue-600" />
                      <div>
                        <span className="text-xs text-blue-600">Ambulance Number:</span>
                        <span className="text-sm font-medium text-blue-900 ml-2">
                          {displayDriver?.ambulanceNumber || allocationStatus?.vehicle?.number || 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          
          {/* Vehicle Information - Use real-time data from polling */}
          {allocationStatus?.vehicle && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Ambulance className="w-4 h-4 text-gray-600" />
                Vehicle Information
                <span className="text-xs text-green-600 font-normal">(Live)</span>
              </h4>
              <div className="space-y-2">
                {allocationStatus.vehicle.number && (
                  <div>
                    <span className="text-xs text-gray-500">Vehicle Number:</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">
                      {allocationStatus.vehicle.number}
                    </span>
                  </div>
                )}
                {allocationStatus.vehicle.type && (
                  <div>
                    <span className="text-xs text-gray-500">Vehicle Type:</span>
                    <span className="text-sm font-medium text-gray-900 ml-2">
                      {allocationStatus.vehicle.type}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Allocation Status - Show real-time status from API */}
          {allocationStatus && (
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <h4 className="text-xs font-semibold text-purple-700 mb-2">
                Allocation Status
                <span className="ml-2 text-xs text-green-600 font-normal">(Live)</span>
              </h4>
              <div className="space-y-1">
                {allocationStatus.ui_state && (
                  <div>
                    <span className="text-xs text-purple-600">Status:</span>
                    <span className="text-xs font-medium text-purple-900 ml-2">
                      {allocationStatus.ui_state}
                    </span>
                  </div>
                )}
                {allocationStatus.driver_response?.status && (
                  <div>
                    <span className="text-xs text-purple-600">Driver Response:</span>
                    <span className="text-xs font-medium text-purple-900 ml-2">
                      {allocationStatus.driver_response.status}
                    </span>
                  </div>
                )}
                {allocationStatus.allocation_type && (
                  <div>
                    <span className="text-xs text-purple-600">Allocation Type:</span>
                    <span className="text-xs font-medium text-purple-900 ml-2">
                      {allocationStatus.allocation_type}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with Cancel Button */}
        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={handleClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomaticAssignmentPopup;

